import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import RenderingTile from 'components/molecules/RenderingTile';
import { reduceCreativesData, reduceNameBasedData } from 'helpers/data';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { convertToDisplayValue } from 'helpers/strings';

// Charts
import VideoQuartileChart from 'components/molecules/charts/VideoQuartileChart';
import VideoCompletionRateChart from 'components/molecules/charts/VideoCompletionRateChart';
import ActivitiesStackedAreaChart from 'components/molecules/charts/ActivitiesStackedAreaChart';
import GroupedBarChart from 'components/molecules/charts/GroupedBarChart';

// Tables
import ActivitiesTable from 'components/molecules/tables/ActivitiesTable';
import PlacementsTable from 'components/molecules/tables/PlacementsTable';
import ProtectedCreativesReports from 'components/organisms/ProtectedCreativesReports';
import ProtectedTargetingTacticsTable from 'components/organisms/ProtectedTargetingTacticsTable';


class VideoExpandModule extends Component {
    render() {
        const stub = 'l3-site-video';
        const { backfillBuckets, xAxisLabel, entityObjective, showTotalActivities } = this.props;
        const {
            eCPAActivitiesOrClicks, showActivitiesData, primaryActivityField
        } = getActivitiesSettings(entityObjective);
        const hasPlacementsData = get(this.props, 'placements.params.hasData', null);
        const hasTargetingTacticsData = get(this.props, 'channel.targetingTactics.params.hasData', null);
        const hasChannelData = get(this.props, 'channel.hasData', false);
        const hasCreativesData = get(this.props, 'channel.creatives.params.hasData', null);

        const data = get(this.props, 'channel.data', {});
        const placements = get(this.props, 'placements', {});
        const targetingTactics = get(this.props, 'channel.targetingTactics', {});
        const creatives = get(this.props, 'channel.creatives', {});
        const activities = get(this.props, 'activities', {});
        const hasActivities = get(this.props, 'activities.hasData', null);
        const activitiesRenderFlag = hasActivities && (showActivitiesData || showTotalActivities);

        let placementsData = null;
        const placementsParams = get(this.props, 'placements.params', {});
        if (hasPlacementsData) {
            placementsData = reduceNameBasedData({
                data: placements,
                namesArrayKey: 'placements',
                injectedKey: 'placementName'
            });
        }
        let targetingTacticsData = {};
        const targetingTacticsParams = get(this.props, 'channel.targetingTactics.params', {});
        if (hasTargetingTacticsData) {
            targetingTacticsData = reduceNameBasedData({
                data: targetingTactics,
                namesArrayKey: 'targetingTactics',
                injectedKey: 'targetingTacticName'
            });
        }

        let creativesData = null;
        const creativesParams = get(this.props, 'channel.creatives.params', {});

        if (hasCreativesData) {
            creativesData = reduceCreativesData(creatives);
        }

        return (
            <React.Fragment>
                {hasChannelData ? (
                    <VideoQuartileChart
                        title="COMPLETION QUARTILES"
                        exportName={`${stub}-completion-quartiles`}
                        data={data}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        yAxisLabel='COMPLETION QUARTILES' />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData} />
                )}

                {hasChannelData ? (
                    <VideoCompletionRateChart
                        title="COMPLETION RATE TRENDS"
                        exportName={`${stub}-completion-rate-trends`}
                        data={data}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        yAxisLabel='COMPLETION RATE' />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData} />
                )}

                {hasPlacementsData ? (
                    <GroupedBarChart
                        title="PLACEMENT PERFORMANCE"
                        exportName={`${stub}-placement-performance`}
                        data={placementsData.graph}
                        keys={placementsData.sortedKeys}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        y={eCPAActivitiesOrClicks}
                        yAxisLabel={convertToDisplayValue(eCPAActivitiesOrClicks)}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasPlacementsData} />
                )}

                {hasPlacementsData ? (
                    <PlacementsTable
                        title="PLACEMENT ACTIVITY BREAKDOWN"
                        exportName={`${stub}-placement-activity-breakdown`}
                        data={placementsData.table}
                        params={placementsParams}
                        hasData={hasPlacementsData}
                        showTotalActivities={showTotalActivities}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['placement', 'impressions', 'clicks', 'ctr', 'completion_rate', 'cpcv', 'spend'] :
                                ['placement', 'impressions', 'ecpa_activities', 'completion_rate', 'cpcv', 'spend']
                        }
                        defaultSorted={[
                            {
                                id: 'cpcv',
                                desc: true
                            }
                        ]}
                    />
                ) : (
                    <RenderingTile height='400px' show={hasPlacementsData} />
                )}

                <ProtectedTargetingTacticsTable
                    title="TARGETING TACTICS BREAKDOWN"
                    exportName={`${stub}-targeting-tactics-breakdown`}
                    tableType="targetingTactics"
                    data={targetingTacticsData.table}
                    params={targetingTacticsParams}
                    hasData={hasTargetingTacticsData}
                    showTotalActivities={showTotalActivities}
                    accessors={
                        entityObjective === 'ctr' ?
                            ['targetingTactic', 'impressions', 'clicks', 'ctr', 'completion_rate', 'cpcv'] :
                            ['targetingTactic', 'impressions', 'ecpa_activities', 'completion_rate', 'cpcv']
                    }
                    defaultSorted={[
                        {
                            id: 'cpcv',
                            desc: true
                        }
                    ]}
                />

                {activitiesRenderFlag ? (
                    <ActivitiesStackedAreaChart
                        title="ACTIVITY TRENDS"
                        exportName={`${stub}-activity-trends`}
                        exportLimit={10}
                        data={activities.data}
                        keys={activities.rankings[primaryActivityField].map(d => d.name).reverse()}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel={`${convertToDisplayValue(primaryActivityField)}`}
                        y={primaryActivityField}
                        colors={this.props.theme.orderedColors}
                        animateDuration={2000}
                        categories={activities.categories} />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                {activitiesRenderFlag ? (
                    <ActivitiesTable
                        title="WEBSITE ACTIVITY BREAKDOWN"
                        exportName={`${stub}-website-activity-breakdown`}
                        data={activities}
                        showTotalActivities={showTotalActivities}
                        objectives={[entityObjective]}
                        filterField={showTotalActivities ? 'total_activities' : primaryActivityField}
                    />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                <ProtectedCreativesReports
                    exportName={`${stub}-creatives`}
                    hasCreativesData={hasCreativesData}
                    creativesData={creativesData}
                    creativesParams={creativesParams}
                    eCPAActivitiesOrClicks={eCPAActivitiesOrClicks}
                    showTotalActivities={showTotalActivities}
                    accessors={['label', 'startedPlays', 'completed_plays', 'completion_rate', 'cpcv']}
                />
            </React.Fragment>
        );
    }
}


VideoExpandModule.propTypes = {
    xAxisLabel: PropTypes.string,
    backfillBuckets: PropTypes.array,
    channel: PropTypes.object,
    placements: PropTypes.object,
    activities: PropTypes.object
};

export default VideoExpandModule;



// WEBPACK FOOTER //
// ./src/components/organisms/siteDirect/VideoExpandModule.js